import React from 'react';
import { Platform, StyleSheet, Text, View, Pressable } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';
import SkeletonBlock from './SkeletonBlock';
import type { PrioritySegmentData, PriorityView } from './types';

interface TicketsByPriorityCardProps {
  segments: PrioritySegmentData[];
  totalTickets: number;
  view: PriorityView;
  onViewChange: (view: PriorityView) => void;
  /** When true the total / stacked bar / legend are replaced by placeholders. */
  loading?: boolean;
  /** Optional error message to display inside the card when the request failed. */
  error?: string | null;
  hasActiveFilters?: boolean;
}

const TOGGLE_OPTIONS: { key: PriorityView; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'total', label: 'Total' },
];

/** Relative widths for the placeholder stack so it reads as a segmented bar. */
const SKELETON_SEGMENT_FLEX = [4, 3, 2, 1];

/**
 * Loading placeholder for this card's body. The header (title + toggle) stays
 * live above it, so only the total, the stacked bar and the legend shimmer.
 */
const TicketsByPrioritySkeleton: React.FC = () => (
  <View>
    <SkeletonBlock style={styles.skeletonTotal} />

    <View style={[styles.priorityStackRow, styles.skeletonStackRow]}>
      {SKELETON_SEGMENT_FLEX.map((flex, index) => (
        <SkeletonBlock
          key={`skeleton-segment-${index}`}
          style={[styles.skeletonSegment, { flex }]}
        />
      ))}
    </View>

    <View style={styles.priorityLegend}>
      {SKELETON_SEGMENT_FLEX.map((_, index) => (
        <View key={`skeleton-legend-${index}`} style={styles.priorityLegendItem}>
          <SkeletonBlock style={styles.skeletonLegendDot} />
          <SkeletonBlock style={styles.skeletonLegendLabel} />
        </View>
      ))}
    </View>
  </View>
);

const TicketsByPriorityCard: React.FC<TicketsByPriorityCardProps> = ({
  segments,
  totalTickets,
  view,
  onViewChange,
  loading = false,
  error = null,
  hasActiveFilters = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipText, setTooltipText] = React.useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = React.useState<{ x: number; y: number } | null>(null);
  // page coordinates of the pointer (clientX / clientY) — used to position
  // the tooltip fixed to the viewport so it appears next to the cursor.
  const [tooltipPagePos, setTooltipPagePos] = React.useState<{ x: number; y: number } | null>(null);
  const tooltipRef = React.useRef<any>(null);
  const [tooltipWidth, setTooltipWidth] = React.useState<number>(0);
  const chartRef = React.useRef<any>(null);
  const containerRect = React.useRef<{ left: number; top: number; width: number; height: number } | null>(null);

  const handleContainerLayout = () => {
    try {
      if (chartRef.current && typeof (chartRef.current.getBoundingClientRect) === 'function') {
        const r = chartRef.current.getBoundingClientRect();
        containerRect.current = { left: r.left, top: r.top, width: r.width, height: r.height };
      } else if (chartRef.current && typeof chartRef.current.measureInWindow === 'function') {
        chartRef.current.measureInWindow((x: number, y: number, w: number, h: number) => {
          containerRect.current = { left: x, top: y, width: w, height: h };
        });
      }
    } catch (err) {
      // ignore
    }
  };

  const positionFromEvent = (e: any) => {
    try {
      const ne = e?.nativeEvent as any;
      if (ne && typeof ne.clientX === 'number' && typeof ne.clientY === 'number') {
        // Save both container-relative (legacy) and page coordinates. The
        // render will prefer page coordinates so the tooltip sits next to the
        // cursor instead of anchored to the card.
        if (!containerRect.current) handleContainerLayout();
        const left = containerRect.current ? ne.clientX - containerRect.current.left : ne.clientX;
        const top = containerRect.current ? ne.clientY - containerRect.current.top : ne.clientY;
        setTooltipPos({ x: left, y: top });
        setTooltipPagePos({ x: ne.clientX, y: ne.clientY });
      }
    } catch (err) {
      // ignore
    }
  };

  const webHoverHandlers = (label: string, value: string) => ({
    onMouseEnter: (e: any) => {
      positionFromEvent(e);
      setTooltipText(`${label}: ${value}`);
      setTooltipVisible(true);
    },
    onMouseMove: (e: any) => {
      positionFromEvent(e);
      setTooltipText(`${label}: ${value}`);
      setTooltipVisible(true);
    },
    onMouseLeave: () => {
      setTooltipVisible(false);
      setTooltipPos(null);
    },
  });
  const hasData = Array.isArray(segments) && segments.some(s => Number(s.value) > 0) && Number(totalTickets) > 0;
  return (
    <View style={[styles.priorityCard, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
      <View style={styles.priorityHeader}>
        <Text style={[styles.priorityTitle, isDark && { color: colors.text.primary }]}>Tickets by Priority</Text>

        <View style={[styles.priorityToggle, isDark && { backgroundColor: colors.background.elevated }]}>
          {TOGGLE_OPTIONS.map(option => (
            <Text
              key={option.key}
              style={[
                styles.priorityToggleText,
                isDark && { color: colors.text.secondary },
                view === option.key && styles.priorityToggleTextActive,
                view === option.key && isDark && { color: colors.brand.primary, backgroundColor: colors.background.surface },
              ]}
              onPress={() => onViewChange(option.key)}
            >
              {option.label}
            </Text>
          ))}
        </View>
      </View>

      {loading ? (
            <TicketsByPrioritySkeleton />
      ) : (
        <>
              {error ? (
                <View style={styles.noDataWrap}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              ) : !hasData ? (
                <View style={styles.noDataWrap}>
                  <Text style={[styles.noDataText, isDark && { color: colors.text.secondary }]}>
                     {hasActiveFilters
              ? 'Try expanding your date range or removing some filters.'
              : 'Try expanding your date range or removing some filters.'}
                  </Text>
                </View>
              ) : (
            <>
              <Text style={[styles.priorityTotal, isDark && { color: colors.text.primary }]}>
                {totalTickets.toLocaleString()} Total
              </Text>

              <View
                ref={chartRef}
                onLayout={handleContainerLayout}
                style={styles.priorityStackRow}
              >
                {segments.map(segment => {
                  // Calculate if segment width will be too narrow for normal text
                  const totalValue = segments.reduce((sum, s) => sum + s.value, 0);
                  const percentWidth = (segment.value / totalValue) * 100;
                  const estimatedWidth = percentWidth * 7; // rough estimate for typical 700px container
                  
                  // Determine text size based on segment width
                  const isVeryNarrow = estimatedWidth < 30;
                  const isNarrow = estimatedWidth < 50;
                  
                  return (
                    <Pressable
                      key={segment.name}
                      style={[
                        styles.prioritySegment,
                        {
                          flex: Math.max(segment.value, 1),
                          backgroundColor: segment.color,
                        },
                      ]}
                      {...(webHoverHandlers(segment.name, `${segment.value}`) as any)}
                      onHoverIn={(e: any) => {
                        positionFromEvent(e);
                        setTooltipText(`${segment.name}: ${segment.value}`);
                        setTooltipVisible(true);
                      }}
                      onHoverOut={() => {
                        setTooltipVisible(false);
                        setTooltipPos(null);
                      }}
                      onPressIn={(e: any) => {
                        positionFromEvent(e);
                        setTooltipText(`${segment.name}: ${segment.value}`);
                        setTooltipVisible(true);
                      }}
                      onPressOut={() => {
                        setTooltipVisible(false);
                        setTooltipPos(null);
                      }}
                    >
                      {segment.value > 0 ? (
                        <Text style={[
                          styles.prioritySegmentText,
                          isVeryNarrow && styles.prioritySegmentTextXSmall,
                          isNarrow && !isVeryNarrow && styles.prioritySegmentTextSmall,
                        ]}>
                          {isVeryNarrow ? (
                            <>
                              {segment.value.toLocaleString()}
                            </>
                          ) : (
                            <>
                              {segment.value.toLocaleString()}
                              {segment.percentage != null ? `\n${segment.percentage.toFixed(1)}%` : ''}
                            </>
                          )}
                        </Text>
                      ) : null}
                    </Pressable>
                  );
                })}
              </View>
              {tooltipVisible && tooltipText ? (
                <View
                  ref={tooltipRef}
                  onLayout={(ev) => setTooltipWidth(ev.nativeEvent.layout.width)}
                  pointerEvents="none"
                  style={(() => {
                    // If we have page coords, position using fixed viewport
                    // coordinates so the tooltip appears near the cursor and
                    // won't jump when the card moves or the page scrolls.
                    const page = tooltipPagePos;
                    if (page && typeof page.x === 'number' && typeof page.y === 'number') {
                      const padding = 8;
                      const offsetX = 12;
                      const offsetY = 36;
                      const winW = typeof window !== 'undefined' ? window.innerWidth : 0;
                      const winH = typeof window !== 'undefined' ? window.innerHeight : 0;
                      let left = page.x + offsetX;
                      // shift left if it would overflow the viewport
                      if (tooltipWidth && winW) left = Math.min(left, Math.max(padding, winW - tooltipWidth - padding));
                      let top = page.y - offsetY;
                      if (winH) top = Math.min(Math.max(padding, top), winH - padding);
                      return [styles.tooltipOverlayAbsolute, { position: 'fixed', left, top }];
                    }

                    // Fallback: use old container-relative behavior
                    const cw = containerRect.current?.width ?? 0;
                    let leftStyle: any = {};
                    if (tooltipPos && typeof tooltipPos.x === 'number') {
                      let left = tooltipPos.x - (tooltipWidth / 2 || 0);
                      const min = 6;
                      const max = Math.max(6, cw - (tooltipWidth || 0) - 6);
                      if (!isNaN(left)) {
                        left = Math.min(Math.max(left, min), max);
                      } else {
                        left = min;
                      }
                      leftStyle = { left };
                    } else {
                      leftStyle = { left: '50%', transform: [{ translateX: -0.5 * (tooltipWidth || 0) }] };
                    }
                    const top = tooltipPos ? Math.max((tooltipPos.y || 0) - 36, 4) : 6;
                    return [styles.tooltipOverlayAbsolute, { top }, leftStyle];
                  })()}
                >
                  <View style={styles.tooltipBubble}>
                    <Text style={styles.tooltipText}>{tooltipText}</Text>
                  </View>
                </View>
              ) : null}

              <View style={styles.priorityLegend}>
                {segments.map(item => (
                  <View key={item.name} style={styles.priorityLegendItem}>
                    <View
                      style={[
                        styles.priorityLegendDot,
                        { backgroundColor: item.color || '#005CBC' },
                      ]}
                    />

                    <Text style={[styles.priorityLegendLabel, isDark && { color: colors.text.primary }]}>{item.name}</Text>

                    <Text style={[styles.priorityLegendValue, isDark && { color: colors.text.secondary }]}>
                      ({item.value.toLocaleString()})
                    </Text>
                  </View>
                ))}
              </View>
            </>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  priorityCard: {
    backgroundColor: '#FFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    paddingBottom: 30,
    minHeight: 262,
    marginBottom: 16,
  },
  priorityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  priorityTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#1D2939',
  },
  priorityToggle: {
    flexDirection: 'row',
    backgroundColor: '#EAF1FB',
    borderRadius: 8,
    padding: 2,
    gap: 4,
  },
  priorityToggleText: {
    fontSize: 12,
    color: '#344054',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6,
    overflow: 'hidden',
  },
  priorityToggleTextActive: {
    color: '#005CBC',
    backgroundColor: '#FFF',
    fontFamily: FontFamily.medium,
  },
  priorityTotal: {
    marginBottom: 12,
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: '#344054',
  },
  priorityStackRow: {
    flexDirection: 'row',
    height: 60,
    borderRadius: 8,
    overflow: 'hidden',
  },
  prioritySegment: {
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 22,
  },
  prioritySegmentText: {
    color: '#FFF',
    fontFamily: FontFamily.bold,
    textAlign: 'center',
    lineHeight: 22,
    fontSize: 12,
  },
  prioritySegmentTextSmall: {
    fontSize: 10,
    lineHeight: 15,
  },
  prioritySegmentTextXSmall: {
    fontSize: 8,
    lineHeight: 12,
  },
  priorityLegend: {
    marginTop: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 18,
  },
  priorityLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  priorityLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 3,
    marginRight: 8,
  },
  priorityLegendLabel: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#1E293B',
    textTransform: 'capitalize',
  },
  priorityLegendValue: {
    marginLeft: 6,
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#667085',
  },

  // ─── Loading skeleton ────────────────────────────────────────────────────
  skeletonTotal: {
    width: 96,
    height: 14,
    marginBottom: 12,
  },
  skeletonStackRow: {
    gap: 2,
  },
  skeletonSegment: {
    height: '100%',
    borderRadius: 0,
  },
  skeletonLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 3,
    marginRight: 8,
  },
  skeletonLegendLabel: {
    width: 68,
    height: 11,
  },
  noDataWrap: {
    minHeight: 180,
    justifyContent: 'center',
  },
  noDataText: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#7A8088',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#B42318',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tooltipOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -50 }, { translateY: -50 }],
    zIndex: 1000,
  },
  tooltipBubble: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 4,
    padding: 8,
  },
  tooltipText: {
    color: '#FFF',
    fontSize: 12,
    fontFamily: FontFamily.medium,
  },
  tooltipOverlayAbsolute: {
    position: 'absolute',
    zIndex: 1000,
  },
});

export default TicketsByPriorityCard;
